import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import { toast } from 'react-toastify';
import authService from '../services/auth-service';
import notificationService from '../services/notification-service';
import fcmService from '../services/fcm-service';
import logger from '../shared/utils/logger';
import { getErrorMessage } from '../shared/utils/errorMessage';

/**
 * Authentication user interface
 */
interface AuthUser {
  id: number;
  username: string;
  isAdmin: boolean;
  role: string;
} 

/**
 * JWT token decoded structure
 */
interface DecodedToken {
  id: number;
  iat: number;
  exp: number;
  username: string;  
  isAdmin: boolean;
}

/**
 * Result of a login attempt. Carries the specific failure reason so callers can
 * render it, instead of assuming every failure means bad credentials.
 */
export interface LoginResult {
  success: boolean;
  message?: string;
}

/**
 * Auth context interface defining available authentication methods and state
 */
interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Custom hook for using the auth context
 * @returns Authentication context with methods and state
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Auth Provider Component
 * Provides authentication functionality to the application
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  /**
   * Check if token is valid by verifying expiration
   * @param token JWT token string
   * @returns Boolean indicating if token is valid
   */
  const isTokenValid = useCallback((token: string): boolean => {
    try {
      const decoded = jwtDecode<DecodedToken>(token);
      const currentTime = Date.now() / 1000;
      return decoded.exp > currentTime;
    } catch (_error: unknown) {
      return false;
    }
  }, []);

  /**
   * Login function to authenticate user
   * @param username User's username
   * @param password User's password
   * @returns Promise resolving to boolean indicating success
   */
  const login = useCallback(async (username: string, password: string): Promise<LoginResult> => {
    try {
      setIsLoading(true);

      if (!username || !password) {
        const message = 'Username and password are required';
        toast.error(message);
        return { success: false, message };
      }

      const response = await authService.login({ username, password });
      
      if (response && response.data) {
        const token = response.data;
        localStorage.setItem('token', token);
        
        // Decode token to get user info
        const decoded = jwtDecode<DecodedToken>(token);
      
        const userData = {
          id: decoded.id,
          username: decoded.username,
          isAdmin: decoded.isAdmin,
          role: decoded.isAdmin ? 'admin' : 'user'
        };
        
        setUser(userData);
        setIsAuthenticated(true);
        return { success: true };
      } else {
        const message = 'Invalid username or password';
        toast.error(message);
        return { success: false, message };
      }
    } catch (error: unknown) {
      // Surface the real reason (server down, validation, 500) rather than
      // letting the caller assume the credentials were wrong
      const message = getErrorMessage(error, 'Unable to sign in. Please try again.');
      toast.error(message);
      return { success: false, message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Logout function to end user session
   *
   * The order of these steps matters. Every one of them is individually non-fatal, so
   * logout always finishes and clears client state.
   */
  const logout = useCallback(async (): Promise<void> => {
    // 1. De-register this device with the backend FIRST. It needs a still-valid JWT,
    //    and step 3 revokes it — swap the two and this request 401s, which would also
    //    trip the api-client's hard redirect in the middle of logging out.
    try {
      const fcmToken = fcmService.getFCMToken();
      if (fcmToken) {
        await notificationService.unregisterDevice(fcmToken);
      }
    } catch (error: unknown) {
      // Best effort: the server purges every device token in step 3 regardless.
      logger.warn('Device unregister failed during logout (server purge still applies):', error);
    }

    // 2. Kill the token at FCM so Google itself stops delivering to this device,
    //    before any slow network call can hang.
    try {
      await fcmService.teardown();
    } catch (error: unknown) {
      logger.warn('FCM teardown failed during logout:', error);
    }

    // 3. Revoke the session server-side (invalidates all JWTs, purges all device tokens).
    try {
      await authService.logout();
    } catch (error: unknown) {
      // Intentionally silent — still clear client-side auth on backend logout failure
      logger.warn('Logout API call failed (client auth cleared anyway):', error);
    }

    // 4. Clear client state last, so App.tsx's [user] effect cleanup runs after teardown.
    localStorage.removeItem('token');
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  /**
   * Check authentication status using stored token
   * @returns Promise resolving to boolean indicating if user is authenticated
   */
  const checkAuth = useCallback(async (): Promise<boolean> => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token || !isTokenValid(token)) {
        setIsAuthenticated(false);
        setUser(null);
        return false;
      }
      
      // Verify token with backend
      const response = await authService.getCurrentUser();
      
      if (response) {
        setUser(response);
        setIsAuthenticated(true);
        return true;
      } else {
        localStorage.removeItem('token');
        setUser(null);
        setIsAuthenticated(false);
        return false;
      }
    } catch (error: unknown) {
      localStorage.removeItem('token');
      setUser(null);
      setIsAuthenticated(false);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isTokenValid]);

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    checkAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;